import { CheckCircle, CircleOffIcon, InfoIcon, Sparkles } from "lucide-react";
import { Duration } from "luxon";

import { Dialog, DialogContent, DialogHeader, DialogPanel, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import type { ClaimWithRelations } from "../../api/use-claim";
import {
  type AiFlightEntry,
  calculateFlightDelta,
  getSequenceLabel,
  groupBySequence,
} from "../../lib/incident-delta";
import { Markdown } from "../../lib/markdown";
import { AiIncidentFlightCard } from "../ai-insights/ai-incident-flight-card";
import { CAUSE_OPTIONS, CONSEQUENCE_OPTIONS } from "../ai-insights/ai-incident-preview";

/**
 * Read-only detail view of a confirmed incident — source, deltas, cause/consequence,
 * AI notes, and the original-vs-actual itinerary comparison. Port of IncidentViewModal.vue.
 */

type Incident = NonNullable<ClaimWithRelations["legalSpecification"]["incident"]>;
type IncidentFlight = NonNullable<Incident["originalItinerary"]>["incidentFlights"][number];

const SOURCE_LABELS: Record<string, string> = {
  GDS: "GDS",
  FLIGHT_MONITORING: "Monitoring",
  COMBO: "GDS + Monitoring",
  MANUAL: "Manuel",
};

const SOURCE_BADGE_CLASSES: Record<string, string> = {
  GDS: "bg-blue-100 text-blue-800",
  FLIGHT_MONITORING: "bg-purple-100 text-purple-800",
  COMBO: "bg-indigo-100 text-indigo-800",
  MANUAL: "bg-slate-100 text-slate-800",
};

function formatDelta(deltaInMinutes: number) {
  const duration = Duration.fromObject({ minutes: Math.abs(deltaInMinutes) }, { locale: "fr" }).shiftTo(
    "hours",
    "minutes",
  );
  return duration.toHuman({ unitDisplay: "long" });
}

function toFlightEntry(entry: IncidentFlight): AiFlightEntry {
  return {
    sequence: entry.sequence,
    order: entry.order,
    segment: entry.segment?.uuid ? { segmentUuid: entry.segment.uuid } : undefined,
    flight: entry.flight
      ? ({
          flightIdentifier: entry.flight.flightIdentifier,
          departureAirport: entry.flight.departureAirport.iata,
          departureAirportName: entry.flight.departureAirport.name,
          departureAirportCity: entry.flight.departureAirport.city,
          arrivalAirport: entry.flight.arrivalAirport.iata,
          arrivalAirportName: entry.flight.arrivalAirport.name,
          arrivalAirportCity: entry.flight.arrivalAirport.city,
          scheduledDeparture: entry.flight.scheduledDepartureLocal,
          scheduledArrival: entry.flight.scheduledArrivalLocal,
          actualDeparture: entry.flight.actualDepartureLocal,
          actualArrival: entry.flight.inGateArrivalLocal,
          monitoringStatus: entry.flight.monitoringStatus,
          operatingAirline: entry.flight.operatingAirline,
          operatingAirlineName: entry.flight.operatingAirline?.name,
        } as unknown as AiFlightEntry["flight"])
      : null,
  };
}

interface IncidentViewModalProps {
  incident: Incident;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function IncidentViewModal({ incident, open, onOpenChange }: IncidentViewModalProps) {
  const originalFlights = (incident.originalItinerary?.incidentFlights ?? []).map(toFlightEntry);
  const rawActualFlights = incident.actualItinerary?.incidentFlights ?? [];
  const actualFlights = rawActualFlights.map((entry) => {
    const original = incident.originalItinerary?.incidentFlights.find(
      (candidate) => candidate.sequence === entry.sequence && candidate.order === entry.order,
    );
    const actualEntry = toFlightEntry(entry);
    return {
      ...actualEntry,
      delta: original ? calculateFlightDelta(actualEntry, toFlightEntry(original)) : undefined,
    };
  });

  const groupedOriginal = groupBySequence(originalFlights);
  const groupedActual = groupBySequence(actualFlights);
  const hasActualItinerary = actualFlights.length > 0;
  const hasDeltas = Boolean(incident.actualDepartureDelta || incident.actualArrivalDelta);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-7xl">
        <DialogHeader>
          <DialogTitle>Détails de l'incident</DialogTitle>
        </DialogHeader>
        <DialogPanel>
          <div className="min-h-full bg-gradient-to-br from-slate-50 to-blue-50 p-4">
            {/* Success indicator */}
            <div className="mb-6 flex items-center justify-center gap-3 rounded-xl border border-green-200 bg-green-50 p-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
              <p className="text-lg font-semibold text-green-800">Incident enregistré</p>
            </div>

            {/* Metadata */}
            <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <InfoIcon className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-slate-800">Métadonnées de l'incident</h3>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div>
                  <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                    Source
                  </div>
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium",
                      SOURCE_BADGE_CLASSES[incident.source] ?? "bg-slate-100 text-slate-800",
                    )}
                  >
                    {SOURCE_LABELS[incident.source] ?? incident.source}
                  </span>
                </div>

                <div>
                  <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                    Type de trajet
                  </div>
                  <div className="text-sm font-medium text-slate-800">
                    {incident.isMultiLeg
                      ? "Multi-escale"
                      : incident.isReturn
                        ? "Aller-retour"
                        : "Aller simple"}
                  </div>
                </div>

                <div>
                  <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                    Aéroports
                  </div>
                  <div className="text-sm font-medium text-slate-800">
                    {incident.firstAirport?.iata} → {incident.lastAirport?.iata}
                  </div>
                  <div className="text-xs text-slate-500">
                    {incident.firstAirport?.city} → {incident.lastAirport?.city}
                  </div>
                </div>

                {hasDeltas ? (
                  <div>
                    <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                      Retard
                    </div>
                    <div className="text-sm font-medium text-slate-800">
                      {incident.actualDepartureDelta ? (
                        <div>Départ: {formatDelta(incident.actualDepartureDelta)}</div>
                      ) : null}
                      {incident.actualArrivalDelta ? (
                        <div>Arrivée: {formatDelta(incident.actualArrivalDelta)}</div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <div>
                  <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Cause</div>
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800">
                    {CAUSE_OPTIONS.find((option) => option.value === incident.cause)?.label ?? incident.cause}
                  </span>
                </div>
                <div>
                  <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                    Conséquence
                  </div>
                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800">
                    {CONSEQUENCE_OPTIONS.find((option) => option.value === incident.consequence)?.label ??
                      incident.consequence}
                  </span>
                </div>
              </div>
            </div>

            {/* AI notes */}
            {incident.notes ? (
              <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center gap-3">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-slate-800">Notes de l'incident</h3>
                </div>
                <Markdown text={incident.notes} className="max-w-none text-sm text-gray-700" />
              </div>
            ) : null}

            {/* Comparison grid */}
            <div className="grid gap-8 px-4 lg:grid-cols-2">
              <div className="space-y-6">
                <div className="mb-6 flex items-center gap-3">
                  <div className="h-3 w-3 rounded-full bg-blue-500" />
                  <h2 className="text-xl font-semibold text-slate-800">Réservation prévue</h2>
                  <span className="rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700">
                    {originalFlights.length} vols
                  </span>
                </div>

                {groupedOriginal.map(([sequenceNum, sequence]) => (
                  <div key={sequenceNum} className="space-y-4">
                    <div className="mb-4 flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
                        {sequenceNum}
                      </div>
                      <span className="font-medium text-slate-700">
                        {getSequenceLabel(sequence[0].sequence)}
                      </span>
                    </div>
                    <div className="overflow-hidden rounded-2xl border-8 border-blue-200 bg-white shadow-sm">
                      {sequence.map((flight, index) => (
                        <AiIncidentFlightCard
                          // biome-ignore lint/suspicious/noArrayIndexKey: ordered static list
                          key={index}
                          flight={flight}
                          nextFlight={sequence[index + 1]}
                          type="original"
                          isFirst={index === 0}
                          isLast={index === sequence.length - 1}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-6">
                <div className="mb-6 flex items-center gap-3">
                  <div className="h-3 w-3 rounded-full bg-amber-500" />
                  <h2 className="text-xl font-semibold text-slate-800">Itinéraire Final</h2>
                  <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-700">
                    {actualFlights.length} vols
                  </span>
                </div>

                {hasActualItinerary
                  ? groupedActual.map(([sequenceNum, sequence]) => (
                      <div key={sequenceNum} className="space-y-4">
                        <div className="mb-4 flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-sm font-bold text-white">
                            {sequenceNum}
                          </div>
                          <span className="font-medium text-slate-700">
                            {getSequenceLabel(sequence[0].sequence)}
                          </span>
                        </div>
                        <div className="overflow-hidden rounded-2xl border-8 border-amber-200 bg-white shadow-sm">
                          {sequence.map((flight, index) => (
                            <AiIncidentFlightCard
                              // biome-ignore lint/suspicious/noArrayIndexKey: ordered static list
                              key={index}
                              flight={flight}
                              nextFlight={sequence[index + 1]}
                              type="actual"
                              isFirst={index === 0}
                              isLast={index === sequence.length - 1}
                            />
                          ))}
                        </div>
                      </div>
                    ))
                  : groupedOriginal.map(([sequenceNum, sequence]) => (
                      <div key={sequenceNum} className="space-y-4">
                        <div className="mb-4 flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-sm font-bold text-white">
                            {sequenceNum}
                          </div>
                          <span className="font-medium text-slate-700">
                            {getSequenceLabel(sequence[0].sequence)}
                          </span>
                        </div>
                        <div className="overflow-hidden rounded-2xl border-8 border-amber-200 bg-white p-8 text-center shadow-sm">
                          <CircleOffIcon className="mx-auto mb-3 h-12 w-12 text-slate-400" />
                          <p className="font-medium text-slate-600">Aucun réacheminement accepté</p>
                          <p className="mt-1 text-sm text-slate-500">Le passager n'a pas été ré-acheminé</p>
                        </div>
                      </div>
                    ))}
              </div>
            </div>
          </div>
        </DialogPanel>
      </DialogContent>
    </Dialog>
  );
}
